import React from 'react';
import styled from 'styled-components';
import {AccentColorOptions, useBreakpointUp, useBreakpointDown} from 'epic-ue-theme';
import {
    AnimatedHeading,
    GridContainer,
    CenteredGrid,
    VerticalSpacer,
    ImageCard,
    ImageCardProps
} from '../../elements';
import Grid from '@material-ui/core/Grid';
import {
    OverviewGridBottomLeft,
    OverviewGridBottomLeftOptionsArray
} from './OverviewGrid.bottomleft';
import {
    SubnavSectionName,
    SubnavSectionConfigProps,
    noVerticalPadding
} from '../../elements/subnav';
import {Spotlight} from '../../wraps/spotlight/Spotlight.wrap';
import {AnimateOnViewWrap} from '../../wraps/animation/AnimateOnViewWrap';

const StyledOverview = styled.div`
    .ue-outlined-card {
        margin: 0 0 0 auto;
        ${(p) => p.theme.breakpoints.up('lg')} {
            max-width: ${(p) => p.theme.spacing(52)}px;
        }
    }
`;
export interface OverviewGridSectionProps {
    accentColor?: AccentColorOptions;
    topLeftSlot?: ImageCardProps; //Upper Left
    bottomLeftSlot?: OverviewGridBottomLeftOptionsArray; //Bottom Left
    topRightSlot?: ImageCardProps; //Upper right
    bottomRightSlot?: ImageCardProps; //Bottom right
    heading?: string;
    sectionConfig: SubnavSectionConfigProps;
    hidden?: boolean;
    componentMapperIndex?: number;
}

export const OverviewGridSection = ({
    sectionConfig,
    accentColor,
    heading = '',
    topLeftSlot,
    bottomLeftSlot,
    topRightSlot,
    bottomRightSlot,
    hidden = false,
    componentMapperIndex
}: OverviewGridSectionProps): JSX.Element | null => {
    const mdUp = useBreakpointUp('md');
    const smDown = useBreakpointDown('sm');
    if (hidden) return null;

    if (bottomRightSlot) delete bottomRightSlot._type;

    const validBottomRightSlot =
        !!bottomRightSlot && !!Object.keys(bottomRightSlot).length && !!bottomRightSlot?.image?.src;
    const validBottomLeftSlot =
        !!bottomLeftSlot &&
        Array.isArray(bottomLeftSlot) &&
        bottomLeftSlot.length &&
        bottomLeftSlot[0] &&
        !!Object.keys(bottomLeftSlot[0]).length;
    const secondRowReplaceBottom =
        (validBottomLeftSlot && !validBottomRightSlot) ||
        (!validBottomLeftSlot && validBottomRightSlot);
    const {sectionName, sectionNameAlign, color, index} = sectionConfig;
    const sectionNameProps = {
        accentColor,
        color,
        sectionName,
        sectionNameAlign
    };

    return (
        <StyledOverview>
            <GridContainer>
                <CenteredGrid direction="row" justifyContent="flex-start" alignItems="flex-start">
                    {/* Left column containing top left and bottom left slots  */}
                    <Grid item xs={12} md={5}>
                        <CenteredGrid>
                            {mdUp ? <Grid item md={2} /> : ''}
                            <Grid item xs={12} md={10}>
                                {componentMapperIndex === 0 ? (
                                    <SubnavSectionName {...sectionNameProps} index={index} />
                                ) : (
                                    <VerticalSpacer
                                        variant="custom"
                                        spacing={4}
                                        downSmSpacing={0}
                                    />
                                )}
                                {heading && <AnimatedHeading heading={heading} />}
                                {smDown && <VerticalSpacer variant="custom" spacing={5} />}
                            </Grid>

                            <Grid item xs={12} style={noVerticalPadding}>
                                <VerticalSpacer variant="custom" spacing={16} downSmSpacing={0} />
                                <AnimateOnViewWrap translateY={30}>
                                    <Spotlight
                                        image={topLeftSlot?.image}
                                        variant="medium"
                                        offsetRatio={[-0.3, -0.4]}
                                        optMaxWidth={900}
                                        disable={smDown}>
                                        <ImageCard
                                            {...topLeftSlot}
                                            paragraphCaption={false}
                                            optMaxWidth={900}
                                            hideBottomPadding={smDown ? true : false}
                                        />
                                    </Spotlight>
                                </AnimateOnViewWrap>
                            </Grid>

                            {validBottomLeftSlot && !secondRowReplaceBottom && (
                                <>
                                    {mdUp ? <Grid item md={2} /> : ''}
                                    <Grid item xs={12} md={10} style={noVerticalPadding}>
                                        <VerticalSpacer variant="layout" />
                                        <AnimateOnViewWrap translateY={30} duration={1.5}>
                                            <OverviewGridBottomLeft
                                                dynamicPageDataArray={bottomLeftSlot}
                                                imageRatioOverride="square"
                                                accentColor={accentColor}
                                                hideBottomPadding={smDown ? true : false}
                                            />
                                        </AnimateOnViewWrap>
                                    </Grid>
                                </>
                            )}
                        </CenteredGrid>
                    </Grid>

                    {/* Center column which acts as a spacer  */}
                    {mdUp ? <Grid item md={1} /> : ''}

                    {/* Right column containing top right and bottom right slots  */}
                    <Grid item xs={12} md={6}>
                        <CenteredGrid>
                            <Grid item xs={12} style={noVerticalPadding}>
                                {heading ? (
                                    <VerticalSpacer variant="layout" />
                                ) : (
                                    <VerticalSpacer
                                        variant="custom"
                                        spacing={4}
                                        downSmSpacing={7}
                                    />
                                )}
                                <AnimateOnViewWrap translateY={30}>
                                    <Spotlight
                                        image={topRightSlot?.image}
                                        variant="medium"
                                        offsetRatio={[0.4, 0.5]}
                                        optMaxWidth={900}
                                        disable={smDown}>
                                        <ImageCard
                                            image={topRightSlot?.image}
                                            caption={topRightSlot?.caption}
                                            imageRatioOverride="square"
                                            optMaxWidth={900}
                                            hideBottomPadding={smDown ? true : false}
                                        />
                                    </Spotlight>
                                </AnimateOnViewWrap>
                            </Grid>
                            <Grid item xs={12} md={10} style={noVerticalPadding}>
                                <AnimateOnViewWrap translateY={30}>
                                    <ImageCard
                                        {...topRightSlot}
                                        image={undefined}
                                        paragraphCaption={false}
                                        optMaxWidth={900}
                                    />
                                </AnimateOnViewWrap>
                                {validBottomRightSlot && !secondRowReplaceBottom ? (
                                    <>
                                        <VerticalSpacer variant="layout" />
                                        <AnimateOnViewWrap translateY={30}>
                                            <ImageCard
                                                {...bottomRightSlot}
                                                paragraphCaption={false}
                                                optMaxWidth={900}
                                                hideBottomPadding={smDown ? true : false}
                                            />
                                        </AnimateOnViewWrap>
                                    </>
                                ) : (
                                    ''
                                )}
                            </Grid>
                            {validBottomRightSlot && !secondRowReplaceBottom && mdUp ? (
                                <Grid item md={2} />
                            ) : (
                                ''
                            )}
                        </CenteredGrid>
                    </Grid>

                    {/* Second row when one of the bottom slots is invalid */}
                    {validBottomLeftSlot && secondRowReplaceBottom && (
                        <Grid item xs={12}>
                            <CenteredGrid>
                                {mdUp ? <Grid item md={2} /> : ''}
                                <Grid item xs={12} md={7} style={noVerticalPadding}>
                                    <VerticalSpacer variant="layout" />
                                    <AnimateOnViewWrap translateY={30}>
                                        <OverviewGridBottomLeft
                                            dynamicPageDataArray={bottomLeftSlot}
                                            imageRatioOverride="rect"
                                            accentColor={accentColor}
                                        />
                                    </AnimateOnViewWrap>
                                </Grid>
                                {mdUp ? <Grid item md={3} /> : ''}
                            </CenteredGrid>
                        </Grid>
                    )}
                    {validBottomRightSlot && secondRowReplaceBottom && (
                        <Grid item xs={12}>
                            <CenteredGrid>
                                {mdUp ? <Grid item md={2} /> : ''}
                                <Grid item xs={12} md={7} style={noVerticalPadding}>
                                    <VerticalSpacer variant="layout" />
                                    <AnimateOnViewWrap translateY={30}>
                                        <ImageCard
                                            {...bottomRightSlot}
                                            paragraphCaption={false}
                                            optMaxWidth={900}
                                            hideBottomPadding={smDown ? true : false}
                                        />
                                    </AnimateOnViewWrap>
                                </Grid>
                                {mdUp ? <Grid item md={3} /> : ''}
                            </CenteredGrid>
                        </Grid>
                    )}
                </CenteredGrid>
                <VerticalSpacer variant="custom" spacing={5} />
            </GridContainer>
        </StyledOverview>
    );
};
